import ReactGA from 'react-ga4';
import { getNogaValueFromLocation } from './analyticsToggleFromQuery';
import { isStoredUserAnalyticsDisabled } from '../logic/authStorage';

const ANALYTICS_DISABLED_KEY = 'analytics_disabled';
const ANALYTICS_DEBUG_KEY = 'analytics_debug';
const ANALYTICS_DEBUG_GLOBAL = '__analyticsDebug__';
const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
const isProduction = process.env.NODE_ENV === 'production';
const shouldUseAnalytics = Boolean(isProduction && measurementId);

let initialized = false;
let disabledLogSent = false;
let debugLogSent = false;

const debugState = {
  enabled: false,
  disabled: false,
  initialized: false,
  initAttempts: 0,
  initBlocked: 0,
  lastInitAttemptAt: null,
  lastInitAt: null,
  sentPageviews: 0,
  sentEvents: 0,
  blockedPageviews: 0,
  blockedEvents: 0,
  lastPageviewAt: null,
  lastEventAt: null,
  lastBlockedAt: null
};

function shouldEnableDebug() {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(ANALYTICS_DEBUG_KEY) === '1';
  } catch (err) {
    return false;
  }
}

function exposeDebugState() {
  if (!shouldEnableDebug()) {
    return;
  }
  debugState.enabled = true;
  window[ANALYTICS_DEBUG_GLOBAL] = { ...debugState };
  if (!debugLogSent) {
    console.info(`[analytics] debug enabled: window.${ANALYTICS_DEBUG_GLOBAL}`);
    debugLogSent = true;
  }
}

function updateDebugState(nextState) {
  Object.assign(debugState, nextState);
  exposeDebugState();
}

function logDisabledOnce() {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  if (disabledLogSent) {
    return;
  }
  // Disabled state can now come from URL query override or persisted localStorage flag.
  console.info('[analytics] disabled via query/localStorage flag');
  disabledLogSent = true;
}

export function isAnalyticsDisabled() {
  if (!shouldUseAnalytics) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  // The server-managed user flag is authoritative and must win over browser-level toggles.
  if (isStoredUserAnalyticsDisabled()) {
    return true;
  }
  // URL-level override is checked first so ?noga=1 works immediately on the current route.
  const nogaValue = getNogaValueFromLocation(window.location);
  if (nogaValue === '1') {
    return true;
  }
  if (nogaValue === '0') {
    return false;
  }
  try {
    return window.localStorage.getItem(ANALYTICS_DISABLED_KEY) === '1';
  } catch (err) {
    return false;
  }
}

export function initAnalytics() {
  if (!shouldUseAnalytics || initialized) {
    updateDebugState({ initialized, disabled: isAnalyticsDisabled() });
    return;
  }
  const disabled = isAnalyticsDisabled();
  updateDebugState({
    disabled,
    initAttempts: debugState.initAttempts + 1,
    lastInitAttemptAt: Date.now()
  });
  if (disabled) {
    logDisabledOnce();
    updateDebugState({
      initBlocked: debugState.initBlocked + 1,
      lastBlockedAt: Date.now()
    });
    return;
  }
  ReactGA.initialize(measurementId);
  initialized = true;
  updateDebugState({
    initialized: true,
    lastInitAt: Date.now()
  });
}

export function pageview(path) {
  if (!shouldUseAnalytics) {
    return;
  }
  const disabled = isAnalyticsDisabled();
  updateDebugState({ disabled });
  if (disabled) {
    logDisabledOnce();
    updateDebugState({
      blockedPageviews: debugState.blockedPageviews + 1,
      lastBlockedAt: Date.now()
    });
    return;
  }
  if (!initialized) {
    initAnalytics();
    if (!initialized) {
      return;
    }
  }
  ReactGA.send({ hitType: 'pageview', page: path });
  updateDebugState({
    sentPageviews: debugState.sentPageviews + 1,
    lastPageviewAt: Date.now()
  });
}

export function event(payload) {
  if (!shouldUseAnalytics) {
    return;
  }
  const disabled = isAnalyticsDisabled();
  updateDebugState({ disabled });
  if (disabled) {
    logDisabledOnce();
    updateDebugState({
      blockedEvents: debugState.blockedEvents + 1,
      lastBlockedAt: Date.now()
    });
    return;
  }
  if (!initialized) {
    initAnalytics();
    if (!initialized) {
      return;
    }
  }
  ReactGA.event(payload);
  updateDebugState({
    sentEvents: debugState.sentEvents + 1,
    lastEventAt: Date.now()
  });
}
